import {
  BrowserCacheLocation,
  LogLevel,
  type Configuration,
  type SilentRequest,
} from '@azure/msal-browser';
import type { AuthConfigOut } from '../types';

/**
 * Builds MSAL's configuration from what the API told us at runtime. Pure and separate
 * from the provider so the wiring can be asserted without standing up MSAL.
 *
 * PKCE is not a flag here: `PublicClientApplication` only implements authorization code
 * with PKCE. There is no implicit-flow option left to get wrong.
 */
export function msalConfiguration(config: AuthConfigOut): Configuration {
  return {
    auth: {
      clientId: config.client_id,
      // Tenant-scoped, never `/common`: `/common` would let any Microsoft account start
      // a sign-in and only fail at token validation, which reads as a broken login.
      authority: `https://login.microsoftonline.com/${config.tenant_id}`,
      // The SPA is served from the API's own origin at the root, so the origin is the
      // redirect URI. It must match the app registration exactly, including no trailing
      // path — Entra compares strings, not URLs.
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // localStorage, not sessionStorage: silent renewal needs the refresh token to
      // survive a reload, and the hidden-iframe fallback that would otherwise cover it
      // is broken by third-party cookie blocking. sessionStorage would mean an
      // interactive sign-in per tab. The cost is that a successful XSS can read the
      // token — the same is true of sessionStorage within the tab it applies to.
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        // MSAL's logger emits tokens and usernames when PII is enabled; it is off by
        // default and stays off. Warnings and errors only, so a working sign-in is quiet.
        piiLoggingEnabled: false,
        // Info, not Warning: MSAL reports a *silent* refusal — a dropped redirect response,
        // a cache it declined to write — at info level, and those are exactly the failures
        // that presented as a redirect loop with an empty console. Still never Verbose,
        // which is noisy enough to bury the line that matters.
        logLevel: LogLevel.Info,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error(`[msal] ${message}`);
          else if (level === LogLevel.Warning) console.warn(`[msal] ${message}`);
          else console.info(`[msal] ${message}`);
        },
      },
    },
  };
}

/**
 * The API's own scope, never a bare client ID. A bare client ID yields a Graph token
 * whose `aud` our API correctly rejects — a failure that looks like a broken login
 * rather than a misconfigured scope. `.default` cannot be mixed with other scopes,
 * so this array holds exactly one entry.
 */
export function tokenRequest(config: AuthConfigOut): SilentRequest {
  return { scopes: [config.api_scope] };
}
